import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MapPin, Calendar, Globe, Building2, ArrowLeft, Flag, Loader2, CheckCircle, Mail, Phone, ExternalLink, FileText, Briefcase, Users, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { DocumentUpload } from '@/components/opportunities/DocumentUpload';
import { countryName } from '@/lib/countries';

type SectorType = 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
type SpaPillarType = 'critical_minerals' | 'industrialization' | 'infrastructure' | 'governance';

interface OpportunityData {
  id: string;
  title: string;
  description: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: SectorType;
  country: string;
  city: string | null;
  remote: boolean;
  eligibility: string;
  spa_linked: boolean;
  spa_pillar: SpaPillarType | null;
  deadline: string | null;
  document_url: string | null;
  created_at: string;
  published_at: string | null;
  company: {
    id: string;
    name: string;
    logo_url: string | null;
    country: string;
    website: string | null;
    description: string | null;
    user_id: string | null;
  };
}

export default function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const navigate = useNavigate();

  const [opportunity, setOpportunity] = useState<OpportunityData | null>(null);
  const [loading, setLoading] = useState(true);

  // Professional application state
  const [hasApplied, setHasApplied] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [coverMessage, setCoverMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [professionalId, setProfessionalId] = useState<string | null>(null);

  // Company bid state
  const [userCompany, setUserCompany] = useState<{ id: string; company_type: string | null } | null>(null);
  const [hasBid, setHasBid] = useState(false);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidMessage, setBidMessage] = useState('');
  const [bidDocumentUrl, setBidDocumentUrl] = useState<string | null>(null);
  const [bidding, setBidding] = useState(false);

  // Supplier contact state (for service_offer)
  const [supplierContact, setSupplierContact] = useState<{ email: string; phone: string | null; website: string | null } | null>(null);

  useEffect(() => {
    if (id) fetchOpportunity();
  }, [id]);

  useEffect(() => {
    if (user && role === 'professional' && id) checkExistingApplication();
    if (user && role === 'company' && id) fetchUserCompany();
  }, [user, role, id]);

  const fetchOpportunity = async () => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .select('*, company:companies(*)')
        .eq('id', id)
        .single();
      if (error) throw error;
      const opp = data as unknown as OpportunityData;
      setOpportunity(opp);

      // For service offers, fetch supplier contact info
      if (opp.type === 'service_offer' && opp.company?.user_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('email, phone')
          .eq('user_id', opp.company.user_id)
          .single();
        if (profile) {
          setSupplierContact({
            email: profile.email,
            phone: profile.phone,
            website: opp.company.website,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching opportunity:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingApplication = async () => {
    if (!user) return;
    try {
      const { data: prof } = await supabase
        .from('professionals')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (!prof) return;
      setProfessionalId(prof.id);

      const { data } = await supabase
        .from('applications')
        .select('id')
        .eq('opportunity_id', id!)
        .eq('professional_id', prof.id)
        .maybeSingle();
      if (data) setHasApplied(true);
    } catch (error) {
      console.error('Error checking application:', error);
    }
  };

  const fetchUserCompany = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('companies')
        .select('id, company_type')
        .eq('user_id', user.id)
        .single();
      if (data) {
        setUserCompany(data);
        // Check existing bid
        const { data: bid } = await supabase
          .from('bids')
          .select('id')
          .eq('opportunity_id', id!)
          .eq('company_id', data.id)
          .maybeSingle();
        if (bid) setHasBid(true);
      }
    } catch (error) {
      console.error('Error fetching user company:', error);
    }
  };

  const handleApply = async () => {
    if (!professionalId || !id) return;
    setApplying(true);
    try {
      const { error } = await supabase.from('applications').insert({
        opportunity_id: id,
        professional_id: professionalId,
        cover_message: coverMessage.trim() || null,
      });
      if (error) throw error;
      setHasApplied(true);
      setApplyDialogOpen(false);
      setCoverMessage('');
      toast.success(t('applications.applySuccess'));
    } catch (error: any) {
      console.error('Error applying:', error);
      toast.error(t('applications.applyError'));
    } finally {
      setApplying(false);
    }
  };

  const handleBid = async () => {
    if (!userCompany || !id) return;
    setBidding(true);
    try {
      const { error } = await supabase.from('bids').insert({
        opportunity_id: id,
        company_id: userCompany.id,
        cover_message: bidMessage.trim() || null,
        document_url: bidDocumentUrl,
      });
      if (error) throw error;
      setHasBid(true);
      setBidDialogOpen(false);
      setBidMessage('');
      setBidDocumentUrl(null);
      toast.success(t('bids.submitSuccess', 'Bid submitted successfully!'));
    } catch (error: any) {
      console.error('Error submitting bid:', error);
      toast.error(t('bids.submitError', 'Failed to submit bid.'));
    } finally {
      setBidding(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!opportunity) {
    return (
      <Layout>
        <div className="container py-12 text-center">
          <h1 className="text-2xl font-bold">Opportunity not found</h1>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/opportunities">Back to Opportunities</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const isB2B = opportunity.type === 'tender' || opportunity.type === 'jv';
  const isServiceOffer = opportunity.type === 'service_offer';
  const isJobOrTraining = opportunity.type === 'job' || opportunity.type === 'training';

  const renderActionCard = () => {
    // SERVICE OFFER → Contact Supplier card
    if (isServiceOffer) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('opportunities.contactSupplier', 'Contact Supplier')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {supplierContact ? (
              <>
                <a
                  href={`mailto:${supplierContact.email}`}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  {supplierContact.email}
                </a>
                {supplierContact.phone && (
                  <a
                    href={`tel:${supplierContact.phone}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {supplierContact.phone}
                  </a>
                )}
                {supplierContact.website && (
                  <a
                    href={supplierContact.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('opportunities.visitWebsite', 'Visit Website')}
                  </a>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('opportunities.contactNotAvailable', 'Contact information is not available.')}
              </p>
            )}
          </CardContent>
        </Card>
      );
    }

    // TENDER / JV → Bid flow for companies
    if (isB2B) {
      return (
        <Card>
          <CardContent className="p-6">
            {user && role === 'company' ? (
              userCompany?.company_type === 'congolese_sme' ? (
                hasBid ? (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <p className="font-medium text-green-700 dark:text-green-400">
                      {t('bids.alreadySubmitted', 'You have already submitted a bid.')}
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/dashboard/company/bids">{t('bids.viewMyBids', 'View My Bids')}</Link>
                    </Button>
                  </div>
                ) : (
                  <Button className="w-full" size="lg" onClick={() => setBidDialogOpen(true)}>
                    {t('bids.submitBid', 'Submit Bid')}
                  </Button>
                )
              ) : (
                <p className="text-center text-sm text-muted-foreground">
                  {t('bids.onlyLocalSuppliers', 'Only local suppliers can bid on this opportunity.')}
                </p>
              )
            ) : user && role === 'professional' ? (
              <p className="text-center text-sm text-muted-foreground">
                {t('bids.companiesOnly', 'Only companies can bid on tenders and joint ventures.')}
              </p>
            ) : (
              <Button asChild className="w-full" size="lg">
                <Link to="/auth?mode=signup">
                  {t('opportunities.detail.signupToApply')}
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }

    // JOB / TRAINING → Standard professional apply
    return (
      <Card>
        <CardContent className="p-6">
          {user && role === 'professional' ? (
            hasApplied ? (
              <div className="flex flex-col items-center gap-2 text-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <p className="font-medium text-green-700 dark:text-green-400">{t('applications.alreadyApplied')}</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/dashboard/professional/applications">{t('applications.viewMyApplications')}</Link>
                </Button>
              </div>
            ) : (
              <Button className="w-full" size="lg" onClick={() => setApplyDialogOpen(true)}>
                {t('opportunities.detail.apply')}
              </Button>
            )
          ) : user ? (
            <p className="text-center text-sm text-muted-foreground">
              {t('applications.onlyProfessionals')}
            </p>
          ) : (
            <Button asChild className="w-full" size="lg">
              <Link to="/auth?mode=signup">
                {t('opportunities.detail.signupToApply')}
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="container py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Button>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <TypeBadge type={opportunity.type} />
                <SectorBadge sector={opportunity.sector} />
                {opportunity.spa_linked && <SpaBadge />}
              </div>
              <h1 className="text-3xl font-bold">{opportunity.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-muted-foreground">
                <Link to={`/companies/${opportunity.company.id}`} className="flex items-center gap-2 hover:text-foreground">
                  <Building2 className="h-4 w-4" />
                  {opportunity.company.name}
                </Link>
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {opportunity.city ? `${opportunity.city}, ` : ''}{countryName(opportunity.country)}
                </span>
                {opportunity.remote && <Badge variant="outline">{t('opportunities.detail.remote')}</Badge>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="secondary">{t(`eligibility.${opportunity.eligibility}`)}</Badge>
              </div>
            </div>

            {opportunity.spa_linked && (
              <Card className="border-accent/50 bg-accent/5">
                <CardContent className="flex items-start gap-4 p-4">
                  <SpaBadge pillar={opportunity.spa_pillar || undefined} />
                  <div>
                    <p className="font-medium">{t('opportunities.detail.spaSupport')}</p>
                    <p className="text-sm text-muted-foreground">
                      {opportunity.spa_pillar && t(`spaPillars.${opportunity.spa_pillar}`)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('opportunities.detail.keyDetails', 'Key Details')}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Briefcase className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('opportunities.detail.type', 'Type')}</p>
                    <p className="font-medium">{t(`opportunities.types.${opportunity.type}`)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('opportunities.detail.sector', 'Sector')}</p>
                    <p className="font-medium">{t(`sectors.${opportunity.sector}`)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('opportunities.detail.location', 'Location')}</p>
                    <p className="font-medium">
                      {opportunity.city ? `${opportunity.city}, ` : ''}{countryName(opportunity.country)}
                      {opportunity.remote && ` (${t('opportunities.detail.remote')})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t('opportunities.detail.eligibility', 'Eligibility')}</p>
                    <p className="font-medium">{t(`eligibility.${opportunity.eligibility}`)}</p>
                  </div>
                </div>
                {opportunity.deadline && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('opportunities.detail.deadline')}</p>
                      <p className="font-medium">{format(new Date(opportunity.deadline), 'MMMM d, yyyy')}</p>
                    </div>
                  </div>
                )}
                {opportunity.published_at && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('opportunities.detail.published', 'Published')}</p>
                      <p className="font-medium">{format(new Date(opportunity.published_at), 'MMMM d, yyyy')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('opportunities.detail.description', 'Description')}</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap">{opportunity.description}</div>
              </CardContent>
            </Card>

          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {renderActionCard()}

            {/* Apply Dialog (jobs/training) */}
            <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('applications.applyTitle')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">{t('applications.applyDescription')}</p>
                  <Textarea
                    placeholder={t('applications.coverMessagePlaceholder')}
                    value={coverMessage}
                    onChange={e => setCoverMessage(e.target.value)}
                    rows={5}
                    maxLength={2000}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setApplyDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleApply} disabled={applying}>
                    {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('applications.submitApplication')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Bid Dialog (tenders/JVs) */}
            <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('bids.submitBid', 'Submit Bid')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('bids.bidDescription', 'Describe your proposal, relevant experience, and why your company is a good fit.')}
                  </p>
                  <Textarea
                    placeholder={t('bids.bidPlaceholder', 'Write your proposal...')}
                    value={bidMessage}
                    onChange={e => setBidMessage(e.target.value)}
                    rows={5}
                    maxLength={3000}
                  />
                  <DocumentUpload
                    label={t('bids.proposalDocument', 'Proposal Document')}
                    description={t('bids.proposalDocumentDescription', 'Upload your formal proposal, pricing, or technical specifications.')}
                    folder="bid-documents"
                    value={bidDocumentUrl}
                    onChange={setBidDocumentUrl}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBidDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleBid} disabled={bidding}>
                    {bidding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('bids.submit', 'Submit')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Tender Document Card */}
            {isB2B && opportunity.document_url && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('opportunities.tenderDocument', 'Tender Document')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a
                    href={opportunity.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('opportunities.downloadTOR', 'Download TOR / RFP')}
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Company Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('opportunities.aboutCompany')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold">{opportunity.company.name}</h4>
                  <p className="text-sm text-muted-foreground">{countryName(opportunity.company.country)}</p>
                </div>
                {opportunity.company.description && (
                  <p className="text-sm text-muted-foreground line-clamp-4">{opportunity.company.description}</p>
                )}
                {opportunity.company.website && (
                  <a href={opportunity.company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                    <Globe className="h-4 w-4" />
                    {t('opportunities.visitWebsite', 'Visit Website')}
                  </a>
                )}
                <Button asChild variant="outline" className="w-full">
                  <Link to={`/companies/${opportunity.company.id}`}>
                    {t('admin.dashboard.viewProfile', 'View Company Profile')}
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <button className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <Flag className="h-4 w-4" />
              {t('opportunities.detail.report')}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
